import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Trophy, Calendar, Users, MapPin, Clock, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsPage = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, registrationsRes] = await Promise.all([
          fetch(`${API}/events`),
          fetch(`${API}/events/my-registrations`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          })
        ]);

        if (eventsRes.ok) setEvents(await eventsRes.json());
        if (registrationsRes.ok) setMyRegistrations(await registrationsRes.json());
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const handleRegister = async (eventId) => {
    setRegistering(eventId);
    try {
      const response = await fetch(`${API}/events/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ event_id: eventId })
      });

      if (response.ok) {
        toast.success('Registration successful! 🎉');
        // Refresh data
        const [eventsRes, registrationsRes] = await Promise.all([
          fetch(`${API}/events`),
          fetch(`${API}/events/my-registrations`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          })
        ]);
        if (eventsRes.ok) setEvents(await eventsRes.json());
        if (registrationsRes.ok) setMyRegistrations(await registrationsRes.json());
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed');
    } finally {
      setRegistering(null);
    }
  };

  const isRegistered = (eventId) => myRegistrations.some(r => r.event_id === eventId);

  const getEventTypeBadge = (type) => {
    switch (type?.toLowerCase()) {
      case 'tournament': return <Badge className="bg-orange-100 text-orange-800">Tournament</Badge>;
      case 'clinic': return <Badge className="bg-blue-100 text-blue-800">Clinic</Badge>;
      case 'social': return <Badge className="bg-purple-100 text-purple-800">Social</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const EventCard = ({ event, showRegister = true }) => (
    <Card className="card-hover overflow-hidden" data-testid={`event-${event.event_id}`}>
      <div className="h-32 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
        <Trophy className="h-12 w-12 text-primary/50" />
      </div>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          {getEventTypeBadge(event.event_type)}
          <span className="text-lg font-heading font-bold text-primary">₱{event.entry_fee}</span>
        </div>
        
        <h3 className="font-heading font-semibold text-lg mb-2">{event.name}</h3>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{event.description}</p>
        
        <div className="space-y-2 text-sm text-muted-foreground mb-4">
          <p className="flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            {event.date}
          </p>
          <p className="flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            {event.start_time} - {event.end_time}
          </p>
          <p className="flex items-center">
            <Users className="h-4 w-4 mr-2" />
            {event.current_participants}/{event.max_participants} registered
          </p>
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {event.skill_levels?.map((level, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">{level}</Badge>
          ))}
        </div>

        {showRegister && (
          isRegistered(event.event_id) ? (
            <Button disabled className="w-full">
              <Check className="h-4 w-4 mr-1" />
              Registered
            </Button>
          ) : event.current_participants >= event.max_participants ? (
            <Button disabled className="w-full" variant="secondary">Event Full</Button>
          ) : (
            <Button
              onClick={() => handleRegister(event.event_id)}
              disabled={registering === event.event_id}
              className="w-full btn-primary"
              data-testid={`register-${event.event_id}`}
            >
              {registering === event.event_id ? 'Registering...' : 'Register Now'}
            </Button>
          )
        )}

        <p className="text-xs text-muted-foreground text-center mt-3">
          Registration deadline: {event.registration_deadline}
        </p>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="events-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Events & Tournaments</h1>
          <p className="text-muted-foreground">Compete, learn, and have fun!</p>
        </div>

        <Tabs defaultValue="upcoming">
          <TabsList className="mb-6">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming Events</TabsTrigger>
            <TabsTrigger value="registered" data-testid="tab-registered">
              My Registrations ({myRegistrations.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-96 bg-muted animate-pulse rounded-xl"></div>)}
              </div>
            ) : events.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <EventCard key={event.event_id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">No upcoming events</h3>
                <p className="text-muted-foreground">Check back later for new events</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="registered">
            {myRegistrations.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myRegistrations.map((reg) => (
                  reg.event && <EventCard key={reg.registration_id} event={reg.event} showRegister={false} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">No registrations yet</h3>
                <p className="text-muted-foreground">Register for an event to see it here</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default EventsPage;
